// Serialized package-mutation scheduler. Exactly one server mutation runs at a
// time (captures POST/PATCH and package PATCH share one package revision, so
// concurrency guarantees a revision conflict). Pending snapshots coalesce per
// key (screenId) — a queued save for screen B is never overwritten by screen
// A's next snapshot, which is how closing-stage edits used to get dropped.
export function createSerializedMutationQueue(flush, { keyOf = (s) => s?.screenId ?? '__op__' } = {}) {
  const pending = new Map(); // key -> { run, resolvers }
  let inFlight = false;
  let drainResolvers = [];

  function settleDrain() {
    if (!inFlight && pending.size === 0) {
      const resolvers = drainResolvers;
      drainResolvers = [];
      resolvers.forEach((resolve) => resolve());
    }
  }

  async function pump() {
    if (inFlight) return;
    const next = pending.entries().next();
    if (next.done) {
      settleDrain();
      return;
    }
    const [key, entry] = next.value;
    pending.delete(key);
    inFlight = true;
    let result;
    let failure = null;
    try {
      result = await entry.run();
    } catch (error) {
      failure = error;
    } finally {
      inFlight = false;
      for (const settler of entry.settlers) {
        // Autosave flushes report failures through reducer state, so their
        // submitters never reject; exclusive operations (Save draft PATCH)
        // need the real result/rejection.
        if (failure && entry.propagateErrors) settler.reject(failure);
        else settler.resolve(failure ? undefined : result);
      }
      // reset() clears stale entries itself, so whatever is pending now is
      // current work and must keep flowing.
      pump();
    }
  }

  function enqueue(key, run, { propagateErrors = false } = {}) {
    const existing = pending.get(key);
    const settlers = existing ? existing.settlers : [];
    const promise = new Promise((resolve, reject) => settlers.push({ resolve, reject }));
    pending.set(key, { run, settlers, propagateErrors });
    pump();
    return promise;
  }

  let exclusiveSequence = 0;

  return {
    submit(snapshot) {
      return enqueue(keyOf(snapshot), () => flush(snapshot));
    },
    // Runs an arbitrary package mutation (e.g. the package-level PATCH from
    // Save draft) in the same serialized lane as capture saves.
    runExclusive(fn) {
      exclusiveSequence += 1;
      return enqueue(`__exclusive__:${exclusiveSequence}`, fn, { propagateErrors: true });
    },
    flushPending() {
      if (!inFlight && pending.size === 0) return Promise.resolve();
      return new Promise((resolve) => drainResolvers.push(resolve));
    },
    reset() {
      for (const entry of pending.values()) {
        entry.settlers.forEach(({ resolve }) => resolve());
      }
      pending.clear();
      settleDrain();
    },
  };
}

export function createLatestRequestQueue(flush) {
  let inFlight = null;
  let activeToken = null;
  let queued = null;
  let generation = 0;

  async function run(snapshot, runGeneration, token) {
    try {
      await flush(snapshot);
    } finally {
      if (runGeneration !== generation) {
        if (activeToken === token) {
          inFlight = null;
          activeToken = null;
        }
      } else if (queued) {
        const next = queued;
        queued = null;
        await run(next, runGeneration, token);
      } else {
        if (activeToken === token) {
          inFlight = null;
          activeToken = null;
        }
      }
    }
  }

  return {
    submit(snapshot) {
      if (inFlight) {
        queued = snapshot;
        return inFlight;
      }
      const token = Symbol('feedback-autosave');
      activeToken = token;
      inFlight = run(snapshot, generation, token);
      return inFlight;
    },
    flushPending() {
      return inFlight || Promise.resolve();
    },
    reset() {
      generation += 1;
      queued = null;
      inFlight = null;
      activeToken = null;
    },
  };
}
